import React, { useCallback, useState, useMemo, useEffect } from 'react';
import {
  ReactFlow,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Background,
  Controls,
  MiniMap,
  Panel,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Sparkles, Copy, Check, Code, Eye, ChevronDown, ChevronUp } from 'lucide-react';

import CurrencyNode from './nodes/CurrencyNode';
import PreallocationNode from './nodes/PreallocationNode';
import ReserveCurrencyNode from './nodes/ReserveCurrencyNode';
import EraNode from './nodes/EraNode';
import NodeSettingsNode from './nodes/NodeSettingsNode';
import AdvancedOptionsNode from './nodes/AdvancedOptionsNode';
import ERC20Node from './nodes/ERC20Node';
import BridgeConverterNode from './nodes/BridgeConverterNode';
import GatewayConverterNode from './nodes/GatewayConverterNode';
import Sidebar from './Sidebar';
import CurrencyStructureDiagram from './CurrencyStructureDiagram';
import CurrencyFlowDiagram from './CurrencyFlowDiagram';
import { resolveCollisions } from '../utils/resolveCollisions';

const nodeTypes = {
  currency: CurrencyNode,
  preallocation: PreallocationNode,
  reserveCurrency: ReserveCurrencyNode,
  era: EraNode,
  nodeSettings: NodeSettingsNode,
  advancedOptions: AdvancedOptionsNode,
  erc20: ERC20Node,
  bridgeConverter: BridgeConverterNode,
  gatewayConverter: GatewayConverterNode,
};

const CurrencyEditor = ({ currencyType, onBack, preloadedData, viewMode }) => {
  const [showFlowDiagram, setShowFlowDiagram] = useState(true);
  
  // Initialize nodes and edges based on preloaded data or default
  const getInitialNodesAndEdges = useCallback(() => {
    // Handle format from parseCurrencyToEditorFormat (existing currencies)
    const isExistingFormat = preloadedData?.config?.mainConfig || preloadedData?.config?.nodes;
    
    // Get main config - handle both formats
    const mainConfig = isExistingFormat 
      ? preloadedData.config.mainConfig 
      : preloadedData?.config;
    
    const mainNodeData = {
      currencyType,
      name: mainConfig?.name || preloadedData?.name || '',
      proofprotocol: mainConfig?.proofprotocol || (currencyType.id === 'token' ? 1 : (currencyType.id === 'erc20' ? 3 : 1)),
      isMintable: mainConfig?.isMintable || mainConfig?.proofprotocol === 2 || false,
      initialsupply: mainConfig?.initialsupply || 0,
      options: mainConfig?.options,
      gatewayconvertername: mainConfig?.gatewayconvertername,
    };
    
    // Node dimensions for layout calculations
    const getNodeWidth = (type) => {
      switch (type) {
        case 'currency': return 360;
        case 'erc20': return 360;
        case 'era': return 380;
        case 'advancedOptions': return 360;
        case 'nodeSettings': return 360;
        case 'reserveCurrency': return 320;
        case 'preallocation': return 320;
        case 'bridgeConverter': return 400;
        case 'gatewayConverter': return 380;
        default: return 340;
      }
    };
    
    const getNodeHeight = (type) => {
      switch (type) {
        case 'currency': return 200;
        case 'era': return 280;
        case 'advancedOptions': return 220;
        case 'nodeSettings': return 180;
        case 'reserveCurrency': return 380;
        case 'preallocation': return 180;
        case 'erc20': return 220;
        case 'bridgeConverter': return 450;
        case 'gatewayConverter': return 420;
        default: return 200;
      }
    };
    
    // Layout constants
    const horizontalGap = 80; // Gap between columns
    const verticalGap = 40; // Gap between nodes in same column
    const startY = 80;
    const startX = 50;
    
    const initialNodes = [];
    const initialEdges = [];
    
    // Collect nodes by type for better layout
    const leftNodes = []; // reserves, eras, erc20
    const rightNodes = []; // preallocations, nodeSettings, advancedOptions
    
    // Collectors for consolidated nodes
    const erasData = [];
    const nodesSettingsData = [];
    const reservesData = [];
    
    // Handle nodes array from parseCurrencyToEditorFormat
    if (preloadedData?.config?.nodes && Array.isArray(preloadedData.config.nodes)) {
      preloadedData.config.nodes.forEach((node) => {
        const nodeId = `${node.type}-preloaded-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        switch (node.type) {
          case 'reserveCurrency':
            // Collect reserve data for consolidation
            if (node.data.reserves) {
              // New format: already has reserves array
              reservesData.push(...node.data.reserves);
            } else if (node.data.currency) {
              // Old format: individual currency/weight
              reservesData.push({ currency: node.data.currency, weight: node.data.weight || 0.5 });
            }
            break;
          case 'preallocation':
            rightNodes.push({
              id: nodeId,
              type: 'preallocation',
              data: node.data,
            });
            break;
          case 'era':
            erasData.push(node.data);
            break;
          case 'nodeSettings':
            nodesSettingsData.push(node.data);
            break;
          case 'erc20':
            leftNodes.push({
              id: 'erc20-preloaded',
              type: 'erc20',
              data: node.data,
            });
            break;
          case 'advancedOptions':
            rightNodes.push({
              id: 'advancedOptions-preloaded',
              type: 'advancedOptions',
              data: node.data,
            });
            break;
          default:
            break;
        }
      });
      
      if (erasData.length > 0) {
        leftNodes.push({
          id: 'era-consolidated',
          type: 'era',
          data: { eras: erasData },
        });
      }
      
      if (reservesData.length > 0) {
        leftNodes.push({
          id: 'reserveCurrency-consolidated',
          type: 'reserveCurrency',
          data: { reserves: reservesData },
        });
      }
      
      if (nodesSettingsData.length > 0) {
        rightNodes.push({
          id: 'nodeSettings-consolidated',
          type: 'nodeSettings',
          data: { nodes: nodesSettingsData },
        });
      }
    } else {
      // Legacy format handling for examples
      if (preloadedData?.config?.reserves) {
        const legacyReserves = preloadedData.config.reserves.map(reserve => ({
          currency: reserve.currency,
          weight: reserve.weight || 0.5
        }));
        if (legacyReserves.length > 0) {
          leftNodes.push({
            id: 'reserveCurrency-consolidated',
            type: 'reserveCurrency',
            data: { reserves: legacyReserves },
          });
        }
      }
      
      if (preloadedData?.config?.preallocations) {
        preloadedData.config.preallocations.forEach((prealloc, index) => {
          rightNodes.push({
            id: `preallocation-preloaded-${index}`,
            type: 'preallocation',
            data: prealloc,
          });
        });
      }
      
      if (preloadedData?.config?.eras) {
        leftNodes.push({
          id: 'era-consolidated',
          type: 'era',
          data: { eras: preloadedData.config.eras },
        });
      }
      
      if (preloadedData?.config?.nodes && !Array.isArray(preloadedData.config.nodes[0]?.type)) {
        rightNodes.push({
          id: 'nodeSettings-consolidated',
          type: 'nodeSettings',
          data: { nodes: preloadedData.config.nodes },
        });
      }
      
      if (preloadedData?.config?.nativecurrencyid) {
        leftNodes.push({
          id: 'erc20-preloaded',
          type: 'erc20',
          data: {
            address: preloadedData.config.nativecurrencyid,
            parent: 'vETH',
          },
        });
      }
    }
    
    // Calculate positions dynamically based on node widths
    // Left column
    let leftColumnX = startX;
    let leftY = startY;
    let maxLeftWidth = 0;
    
    leftNodes.forEach((node) => {
      const nodeWidth = getNodeWidth(node.type);
      maxLeftWidth = Math.max(maxLeftWidth, nodeWidth);
      
      initialNodes.push({
        ...node,
        position: { x: leftColumnX, y: leftY },
      });
      
      initialEdges.push({
        id: `edge-${node.id}-to-main`,
        source: node.id,
        target: 'currency-main',
        animated: true,
        style: { stroke: '#6366f1', strokeWidth: 2 },
      });
      
      leftY += getNodeHeight(node.type) + verticalGap;
    });
    
    // Main node - positioned after left column with gap
    const mainNodeWidth = getNodeWidth('currency');
    const mainNodeHeight = getNodeHeight('currency');
    const mainNodeX = leftNodes.length > 0 
      ? leftColumnX + maxLeftWidth + horizontalGap 
      : startX;
    
    // Right column - positioned after main node with gap
    const rightColumnX = mainNodeX + mainNodeWidth + horizontalGap;
    let rightY = startY;
    
    rightNodes.forEach((node) => {
      initialNodes.push({
        ...node,
        position: { x: rightColumnX, y: rightY },
      });
      
      initialEdges.push({
        id: `edge-main-to-${node.id}`,
        source: 'currency-main',
        target: node.id,
        animated: true,
        style: { stroke: '#6366f1', strokeWidth: 2 },
      });
      
      rightY += getNodeHeight(node.type) + verticalGap;
    });
    
    // Calculate main node Y to center it vertically
    const leftColumnHeight = leftY - startY;
    const rightColumnHeight = rightY - startY;
    const maxColumnHeight = Math.max(leftColumnHeight, rightColumnHeight, mainNodeHeight);
    const mainNodeY = startY + Math.max(0, (maxColumnHeight - mainNodeHeight) / 2);
    
    // Add main node at the calculated position
    initialNodes.unshift({
      id: 'currency-main',
      type: 'currency',
      position: { x: mainNodeX, y: mainNodeY },
      data: mainNodeData,
    });
    
    // Add Bridge Converter node for PBaaS currencies (far right column)
    const bridgeData = preloadedData?.config?.bridgeConverterData || preloadedData?.bridgeConverterData;
    if (bridgeData && currencyType.id === 'pbaas') {
      const bridgeColumnX = rightColumnX + getNodeWidth('advancedOptions') + horizontalGap;
      const bridgeNodeY = startY;
      
      initialNodes.push({
        id: 'bridgeConverter-node',
        type: 'bridgeConverter',
        position: { x: bridgeColumnX, y: bridgeNodeY },
        data: {
          name: bridgeData.name,
          currencyid: bridgeData.currencyid,
          fullyqualifiedname: bridgeData.fullyqualifiedname,
          currencies: bridgeData.currencies,
          weights: bridgeData.weights,
          conversions: bridgeData.conversions,
          initialsupply: bridgeData.initialsupply,
          prelaunchcarveout: bridgeData.prelaunchcarveout,
          gateway: bridgeData.gateway,
          initialcontributions: bridgeData.initialcontributions,
          gatewayconverterissuance: bridgeData.gatewayconverterissuance,
          idregistrationfees: bridgeData.idregistrationfees,
          idreferrallevels: bridgeData.idreferrallevels,
          idimportfees: bridgeData.idimportfees,
          options: bridgeData.options,
          parentName: mainNodeData.name || 'Chain',
        },
      });
      
      // Edge from main node to bridge converter
      initialEdges.push({
        id: 'edge-main-to-bridge',
        source: 'currency-main',
        target: 'bridgeConverter-node',
        animated: true,
        style: { stroke: '#10b981', strokeWidth: 2 },
        label: 'Bridge',
        labelStyle: { fill: '#10b981', fontWeight: 600, fontSize: 12 },
        labelBgStyle: { fill: '#1e293b', fillOpacity: 0.9 },
        labelBgPadding: [4, 4],
        labelBgBorderRadius: 4,
      });
    }
    
    return { nodes: initialNodes, edges: initialEdges };
  }, [currencyType, preloadedData]);

  const { nodes: initialNodes, edges: initialEdges } = getInitialNodesAndEdges();
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [showOutput, setShowOutput] = useState(false);
  const [copied, setCopied] = useState(false);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: '#6366f1' } }, eds)),
    []
  );

  // Resolve collisions when nodes are dragged
  const onNodeDragStop = useCallback(() => {
    setNodes((currentNodes) => {
      const { nodes: resolvedNodes, numIterations } = resolveCollisions(currentNodes, {
        maxIterations: 100,
        overlapThreshold: 0.5,
        margin: 20,
      });
      // Only update if there were actual changes
      return numIterations > 1 ? resolvedNodes : currentNodes;
    });
  }, []);

  // Resolve collisions on initial load
  useEffect(() => {
    // Give nodes time to render and get measured dimensions
    const timer = setTimeout(() => {
      setNodes((currentNodes) => {
        const { nodes: resolvedNodes } = resolveCollisions(currentNodes, {
          maxIterations: 100,
          overlapThreshold: 0.5,
          margin: 25,
        });
        return resolvedNodes;
      });
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const updateNodeData = useCallback((nodeId, newData) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          return { ...node, data: { ...node.data, ...newData } };
        }
        return node;
      })
    );
  }, []);

  const addNode = useCallback((nodeType, position) => {
    const id = `${nodeType}-${Date.now()}`;
    const defaultData = getDefaultNodeData(nodeType);
    
    setNodes((nds) => [
      ...nds,
      {
        id,
        type: nodeType,
        position: position || { x: 100, y: 100 + nds.length * 50 },
        data: { ...defaultData, onUpdate: (data) => updateNodeData(id, data) },
      },
    ]);
  }, [updateNodeData]);

  const getDefaultNodeData = (nodeType) => {
    switch (nodeType) {
      case 'preallocation':
        return { identity: '', amount: 0 };
      case 'reserveCurrency':
        return { reserves: [] };
      case 'era':
        return { reward: 2400000000, decay: 0, halving: 1051920, eraend: 0 };
      case 'nodeSettings':
        return { networkaddress: '', nodeidentity: '' };
      case 'advancedOptions':
        return { 
          idregistrationfees: 100,
          idreferrallevels: 3,
          notarizationreward: 0.0001,
          blocktime: 60,
          powaveragingwindow: 45,
        };
      case 'erc20':
        return { address: '', parent: 'vETH' };
      case 'gatewayConverter':
        return { 
          name: 'Bridge',
          currencies: [
            { currency: 'VRSC', weight: 0.5 },
            { currency: '[CHAIN]', weight: 0.5 }
          ],
          initialsupply: 100000,
          gatewayconverterissuance: 0,
          prelaunchcarveout: 0,
          idregistrationfees: 100,
          idreferrallevels: 3,
        };
      default:
        return {};
    }
  };

  const generateCommand = useMemo(() => {
    const mainNode = nodes.find(n => n.id === 'currency-main');
    if (!mainNode || !mainNode.data.name) return null;

    const currencyDef = {
      name: mainNode.data.name,
      options: currencyType.options,
    };

    // Set proofprotocol based on type and mintable setting
    if (currencyType.id === 'token') {
      currencyDef.proofprotocol = mainNode.data.isMintable ? 2 : 1;
    } else if (currencyType.id === 'erc20') {
      currencyDef.proofprotocol = 3;
      currencyDef.notarizationprotocol = 3;
      currencyDef.parent = 'vETH';
      currencyDef.systemid = 'vETH';
      currencyDef.launchsystemid = 'VRSC';
    } else if (currencyType.id === 'pbaas') {
      currencyDef.proofprotocol = 1;
      currencyDef.notarizationprotocol = 1;
    } else {
      currencyDef.proofprotocol = 1;
    }

    // Process connected nodes
    const preallocations = [];
    const reserveCurrencies = [];
    const weights = [];
    const eras = [];
    const bootstrapNodes = [];
    let advancedOptions = null;
    let erc20Data = null;
    let gatewayConverterData = null;

    nodes.forEach(node => {
      if (node.type === 'preallocation' && node.data.identity && node.data.amount > 0) {
        preallocations.push({ [node.data.identity]: node.data.amount });
      }
      if (node.type === 'reserveCurrency' && node.data.reserves) {
        node.data.reserves.forEach(reserve => {
          if (reserve.currency) {
            reserveCurrencies.push(reserve.currency);
            weights.push(reserve.weight || 0);
          }
        });
      }
      if (node.type === 'era') {
        eras.push({
          reward: node.data.reward,
          decay: node.data.decay,
          halving: node.data.halving,
          eraend: node.data.eraend,
        });
      }
      if (node.type === 'nodeSettings' && node.data.networkaddress) {
        bootstrapNodes.push({
          networkaddress: node.data.networkaddress,
          nodeidentity: node.data.nodeidentity,
        });
      }
      if (node.type === 'advancedOptions') {
        advancedOptions = node.data;
      }
      if (node.type === 'erc20' && node.data.address) {
        erc20Data = node.data;
      }
      if (node.type === 'gatewayConverter') {
        gatewayConverterData = node.data;
      }
    });

    // Add preallocations
    if (preallocations.length > 0) {
      currencyDef.preallocations = preallocations;
    }

    // Add reserve currencies for basket
    if (currencyType.id === 'basket' && reserveCurrencies.length > 0) {
      currencyDef.currencies = reserveCurrencies;
      currencyDef.weights = weights;
      currencyDef.initialsupply = mainNode.data.initialsupply || 100000;
      if (mainNode.data.initialcontributions) {
        currencyDef.initialcontributions = mainNode.data.initialcontributions;
      }
    }

    // Add PBaaS specific options
    if (currencyType.id === 'pbaas') {
      if (reserveCurrencies.length > 0) {
        currencyDef.currencies = reserveCurrencies;
        currencyDef.weights = weights;
      } else {
        currencyDef.currencies = ['VRSC'];
        currencyDef.weights = [1];
      }
      currencyDef.initialsupply = 0;
      
      if (eras.length > 0) {
        currencyDef.eras = eras;
      }
      if (bootstrapNodes.length > 0) {
        currencyDef.nodes = bootstrapNodes;
      }
      
      // Gateway converter configuration
      if (gatewayConverterData) {
        currencyDef.gatewayconvertername = gatewayConverterData.name || 'Bridge';
        if (gatewayConverterData.gatewayconverterissuance > 0) {
          currencyDef.gatewayconverterissuance = gatewayConverterData.gatewayconverterissuance;
        }
      } else if (mainNode.data.gatewayconvertername) {
        currencyDef.gatewayconvertername = mainNode.data.gatewayconvertername;
      }
      
      if (advancedOptions) {
        Object.assign(currencyDef, {
          idregistrationfees: advancedOptions.idregistrationfees,
          idreferrallevels: advancedOptions.idreferrallevels,
          notarizationreward: advancedOptions.notarizationreward,
          blocktime: advancedOptions.blocktime,
          powaveragingwindow: advancedOptions.powaveragingwindow,
        });
      }
    }

    // Add ERC-20 specific options
    if (currencyType.id === 'erc20' && erc20Data) {
      currencyDef.nativecurrencyid = {
        type: 9,
        address: erc20Data.address,
      };
      currencyDef.initialsupply = 0;
      currencyDef.currencies = ['VRSC'];
      currencyDef.maxpreconversion = [0];
      currencyDef.conversions = [1];
    }

    const jsonStr = JSON.stringify(currencyDef);
    return `./verus definecurrency '${jsonStr}'`;
  }, [nodes, currencyType]);

  const copyToClipboard = () => {
    if (generateCommand) {
      navigator.clipboard.writeText(generateCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const mainNode = nodes.find(n => n.id === 'currency-main');
  const isValid = mainNode?.data?.name?.length > 0;

  return (
    <div className="h-screen w-screen bg-slate-900 flex">
      {/* Sidebar */}
      <Sidebar currencyType={currencyType} onAddNode={addNode} />

      {/* Main Editor */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="h-14 bg-slate-800/80 backdrop-blur-sm border-b border-slate-700/50 flex items-center justify-between px-4">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back</span>
            </button>
            <div className="h-6 w-px bg-slate-700" />
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${currencyType.color} flex items-center justify-center`}>
                {currencyType.icon && <currencyType.icon className="w-4 h-4 text-white" />}
              </div>
              <div>
                <h2 className="text-sm font-medium text-white">
                  {currencyType.title}
                  {viewMode && <span className="ml-2 text-xs text-cyan-400">(View Only)</span>}
                </h2>
                <p className="text-xs text-slate-400">{currencyType.subtitle}</p>
              </div>
            </div>
            
            {/* View mode indicator */}
            {viewMode && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <Eye className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-cyan-400">Viewing existing currency</span>
              </div>
            )}
            
            {preloadedData?.isExample && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-xs text-amber-400">Example: {preloadedData.name}</span>
              </div>
            )}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowOutput(true)}
            disabled={!isValid}
            className={`
              flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm
              ${isValid 
                ? `bg-gradient-to-r ${currencyType.color} text-white shadow-lg` 
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }
              transition-all duration-200
            `}
          >
            <Sparkles className="w-4 h-4" />
            {viewMode ? 'View Command' : 'Generate Command'}
          </motion.button>
        </div>

        {/* Flow Editor */}
        <div className="flex-1 relative flex flex-col">
          <div className={`${showFlowDiagram ? 'flex-1' : 'h-full'} relative`}>
            <ReactFlow
              nodes={nodes.map(node => ({
                ...node,
                data: { 
                  ...node.data, 
                  onUpdate: (data) => updateNodeData(node.id, data),
                  currencyType,
                  readOnly: viewMode,
                },
              }))}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeDragStop={onNodeDragStop}
              nodeTypes={nodeTypes}
              defaultViewport={{ x: 20, y: 20, zoom: 0.75 }}
              fitView
              fitViewOptions={{ padding: 0.2, maxZoom: 0.9 }}
              minZoom={0.3}
              maxZoom={2}
              className="bg-slate-900"
            >
              <Background color="#334155" gap={20} size={1} />
              <Controls className="!bg-slate-800 !border-slate-700 !rounded-lg" />
              <MiniMap 
                className="!bg-slate-800 !border-slate-700 !rounded-lg"
                nodeColor={(node) => {
                  if (node.type === 'currency') return '#6366f1';
                  if (node.type === 'preallocation') return '#10b981';
                  if (node.type === 'reserveCurrency') return '#f59e0b';
                  if (node.type === 'era') return '#8b5cf6';
                  if (node.type === 'nodeSettings') return '#ec4899';
                  return '#64748b';
                }}
              />
              <Panel position="top-left" className="!m-4">
                <div className="bg-slate-800/80 backdrop-blur-sm rounded-lg p-3 border border-slate-700/50">
                  <p className="text-xs text-slate-400">
                    {viewMode 
                      ? 'Viewing existing currency configuration'
                      : 'Drag nodes from the sidebar • Connect nodes to build your currency'
                    }
                  </p>
                </div>
              </Panel>
            </ReactFlow>
          </div>

          {/* Flow Diagram Toggle and Display */}
          <div className="border-t border-slate-700/50">
            <button
              onClick={() => setShowFlowDiagram(!showFlowDiagram)}
              className="w-full flex items-center justify-between px-4 py-2 bg-slate-800/50 hover:bg-slate-800/80 transition-colors"
            >
              <span className="text-sm text-slate-400">How This Currency Works</span>
              {showFlowDiagram ? (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronUp className="w-4 h-4 text-slate-400" />
              )}
            </button>
            
            <AnimatePresence>
              {showFlowDiagram && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden bg-slate-800/30"
                >
                  <div className="p-4">
                    <CurrencyFlowDiagram 
                      currencyType={currencyType} 
                      currencyData={mainNode?.data}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Output Modal - Large Popup */}
      <AnimatePresence>
        {showOutput && (
          <div className="fixed inset-0 z-[100]">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/90"
              onClick={() => setShowOutput(false)}
            />

            {/* Scrollable Container */}
            <div className="absolute inset-0 overflow-y-auto p-4">
              <div className="max-w-5xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="bg-slate-800 rounded-2xl border border-slate-700 shadow-2xl"
                >
                  {/* Header with Close Button */}
                  <div className="p-4 border-b border-slate-700 bg-slate-800 rounded-t-2xl flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${currencyType.color} flex items-center justify-center shadow-lg`}>
                        <Code className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{mainNode?.data?.name || 'Currency'} Configuration</h3>
                        <p className="text-sm text-slate-400">{currencyType.title}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowOutput(false);
                      }}
                      className="w-10 h-10 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-black shadow-lg cursor-pointer z-50"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Currency Structure Diagram */}
                  <div className="p-6 bg-slate-900">
                    <CurrencyStructureDiagram nodes={nodes} currencyType={currencyType} />
                  </div>

                  {/* Command Section */}
                  <div className="border-t-2 border-indigo-500 bg-slate-800 rounded-b-2xl">
                    <div className="p-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-indigo-400" />
                        <span className="text-base font-medium text-white">Generated Command</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => copyToClipboard()}
                        className={`
                          flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium cursor-pointer
                          ${copied 
                            ? 'bg-green-500 text-black' 
                            : 'bg-indigo-500 text-black hover:bg-indigo-400'
                          }
                        `}
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy Command
                          </>
                        )}
                      </button>
                    </div>
                    <div className="p-4 bg-slate-950 border-t border-slate-700 rounded-b-2xl overflow-x-auto">
                      <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap break-all overflow-wrap-anywhere" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                        {generateCommand}
                      </pre>
                    </div>
                  </div>
                </motion.div>
                
                {/* Bottom padding for scroll */}
                <div className="h-8" />
              </div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CurrencyEditor;
